const STEP1_SUBMIT_SELECTOR = "#submit-exhibitor-step1"
const SIGNUP_FORM_SELECTOR = "[data-exhibitor-signup-form]"
const ALERT_HOST_ID = "exhibitor-registration-step1-alert"

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const normalizeFieldValue = (value) => String(value || "").trim()

const cleanFieldLabel = (value) => normalizeFieldValue(value).replace(/\s*\*\s*$/u, "")

const getSectionTitle = (input) => {
  let node = input.closest(".form__input_wrapper, .form__flex_inputs, .registration__password-fields")
  while (node) {
    let previous = node.previousElementSibling
    while (previous) {
      if (previous.matches?.(".registration__subtitle")) {
        return normalizeFieldValue(previous.textContent)
      }
      const subtitle = previous.querySelector?.(".registration__subtitle")
      if (subtitle) return normalizeFieldValue(subtitle.textContent)
      previous = previous.previousElementSibling
    }
    node = node.parentElement?.closest(".form__input_wrapper, .form__flex_inputs, .registration__password-fields")
  }
  return null
}

const useSimpleFieldLabels = (form) => form?.matches(SIGNUP_FORM_SELECTOR)

const getFieldLabel = (input, form) => {
  const wrapper = input.closest(".form__input_wrapper")
  const labelText = cleanFieldLabel(
    wrapper?.querySelector(".form__floating_label")?.textContent ||
      document.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.textContent
  )
  if (useSimpleFieldLabels(form)) return labelText || "Champ obligatoire"

  const section = getSectionTitle(input)
  if (labelText && section) return `${labelText} — ${section}`
  return labelText || "Champ obligatoire"
}

const isBillingFieldHidden = (input) => {
  const billingFields = input.closest("[data-billing-fields]")
  return Boolean(billingFields?.hidden)
}

const isRequiredFieldEmpty = (input) => {
  if (input.type === "checkbox" || input.type === "radio") return !input.checked
  return normalizeFieldValue(input.value) === ""
}

const collectMissingRequiredFields = (form) => {
  const missing = []
  const seenLabels = new Set()

  form.querySelectorAll("input, select, textarea").forEach((input) => {
    if (!input.required || input.type === "hidden") return
    if (isBillingFieldHidden(input)) return
    if (!isRequiredFieldEmpty(input)) return

    const label = getFieldLabel(input, form)
    if (seenLabels.has(label)) return
    seenLabels.add(label)
    missing.push(label)
  })

  return missing
}

const collectInvalidEmails = (form) => {
  const invalid = []
  const checks = [
    { id: "exhibitor_user_attributes_email", fallback: "Adresse e-mail" },
    { id: "exhibitor_email", fallback: "E-mail" },
  ]

  checks.forEach(({ id, fallback }) => {
    const input = form.querySelector(`#${CSS.escape(id)}`) || document.getElementById(id)
    if (!input) return
    const value = normalizeFieldValue(input.value)
    if (value === "") return
    if (!value.toLowerCase().match(EMAIL_REGEX)) {
      const label = getFieldLabel(input, form) || fallback
      invalid.push(`${label} (format invalide)`)
    }
  })

  return invalid
}

const collectPasswordErrors = (form) => {
  const password = form.querySelector("#exhibitor_user_attributes_password")
  const confirmation = form.querySelector("#exhibitor_user_attributes_password_confirmation")
  if (!password || !confirmation) return []

  const passwordValue = normalizeFieldValue(password.value)
  const confirmationValue = normalizeFieldValue(confirmation.value)
  if (passwordValue === "" && confirmationValue === "") return []
  if (passwordValue !== confirmationValue) {
    const label =
      getFieldLabel(confirmation, form) ||
      cleanFieldLabel("Confirmation du mot de passe")
    return [`${label} (doit être identique au mot de passe)`]
  }
  return []
}

const getAlertHost = () => document.getElementById(ALERT_HOST_ID)

const hideStep1RequiredAlert = () => {
  const host = getAlertHost()
  if (!host) return
  host.hidden = true
  host.innerHTML = ""
  delete host.dataset.step1ClientAlert
}

const showStep1RequiredAlert = (messages) => {
  const host = getAlertHost()
  if (!host || messages.length === 0) return

  host.replaceChildren()
  host.dataset.step1ClientAlert = "true"

  const title = document.createElement("p")
  title.className = "registration__step1-required-alert-title"
  title.textContent = "Veuillez remplir les champs suivants :"

  const list = document.createElement("ul")
  list.className = "registration__step1-required-alert-list"
  messages.forEach((message) => {
    const item = document.createElement("li")
    item.textContent = message
    list.appendChild(item)
  })

  host.append(title, list)
  host.hidden = false
  host.setAttribute("tabindex", "-1")
  window.scrollTo(0, 0)
  host.focus({ preventScroll: true })
}

const syncStep1RequiredAlert = (form) => {
  const host = getAlertHost()
  if (!host || host.hidden || host.dataset.step1ClientAlert !== "true") return

  const messages = validateExhibitorStep1Form(form)
  if (messages.length === 0) {
    hideStep1RequiredAlert()
    return
  }

  const list = host.querySelector(".registration__step1-required-alert-list")
  if (!list) return

  const remaining = new Set(messages)

  list.querySelectorAll("li").forEach((item) => {
    const text = normalizeFieldValue(item.textContent)
    if (!remaining.has(text)) item.remove()
  })

  const existing = new Set(
    Array.from(list.querySelectorAll("li")).map((item) => normalizeFieldValue(item.textContent))
  )
  messages.forEach((message) => {
    if (existing.has(message)) return
    const item = document.createElement("li")
    item.textContent = message
    list.appendChild(item)
  })

  if (list.children.length === 0) hideStep1RequiredAlert()
}

const validateExhibitorStep1Form = (form) => {
  const messages = [
    ...collectMissingRequiredFields(form),
    ...collectInvalidEmails(form),
    ...collectPasswordErrors(form),
  ]
  return [...new Set(messages)]
}

const formUsesStep1Validation = (form) => {
  if (!(form instanceof HTMLFormElement)) return false
  if (form.matches(SIGNUP_FORM_SELECTOR)) return true
  return Boolean(form.querySelector(STEP1_SUBMIT_SELECTOR))
}

const isSaveOnlySubmit = (event) => {
  const submitter = event.submitter
  return submitter?.name === "save_only" && submitter?.value === "1"
}

const isWizardStep1UpdateForm = (form) =>
  /\/exhibitors\/\d+/.test(form.getAttribute("action") || "")

let step1ValidationBound = false

const bindExhibitorStep1Validation = () => {
  if (step1ValidationBound) return
  step1ValidationBound = true

  document.addEventListener(
    "click",
    (event) => {
      const saveBtn = event.target.closest(".edit-exhibitor-products-save-btn")
      if (!saveBtn) return

      const container = saveBtn.closest(".registration__buttons_container")
      if (!container) return

      const step1Save = container.querySelector("#save-exhibitor-step1")
      if (step1Save && container.querySelector("#submit-exhibitor-step1")) {
        event.preventDefault()
        step1Save.click()
        return
      }

      const commandsSave = container.querySelector("#save-exhibitor-commands")
      if (!commandsSave || !container.querySelector(".edit-commands-submit")) return

      event.preventDefault()
      commandsSave.click()
    },
    true
  )

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target
      if (!formUsesStep1Validation(form)) return
      if (isSaveOnlySubmit(event) && isWizardStep1UpdateForm(form)) {
        hideStep1RequiredAlert()
        return
      }

      const messages = validateExhibitorStep1Form(form)
      if (messages.length === 0) {
        hideStep1RequiredAlert()
        return
      }

      event.preventDefault()
      event.stopPropagation()
      showStep1RequiredAlert(messages)
    },
    true
  )

  const refreshStep1AlertOnFieldChange = (event) => {
    const form = event.target.closest("form")
    if (!formUsesStep1Validation(form)) return
    syncStep1RequiredAlert(form)
  }

  document.addEventListener("input", refreshStep1AlertOnFieldChange, true)
  document.addEventListener("change", refreshStep1AlertOnFieldChange, true)
}

const restoreSignupPasswordFields = () => {
  const form = document.querySelector(SIGNUP_FORM_SELECTOR)
  if (!form || form.dataset.signupRestorePassword !== "1") return

  const password = form.dataset.signupPassword || ""
  const confirmation = form.dataset.signupPasswordConfirmation || password
  const passwordInput = form.querySelector("#exhibitor_user_attributes_password")
  const confirmationInput = form.querySelector("#exhibitor_user_attributes_password_confirmation")

  if (passwordInput && password && !normalizeFieldValue(passwordInput.value)) {
    passwordInput.value = password
    passwordInput.classList.toggle("form__input--filled", true)
  }
  if (confirmationInput && confirmation && !normalizeFieldValue(confirmationInput.value)) {
    confirmationInput.value = confirmation
    confirmationInput.classList.toggle("form__input--filled", true)
  }

  delete form.dataset.signupRestorePassword
  delete form.dataset.signupPassword
  delete form.dataset.signupPasswordConfirmation
}

const initExhibitorStep1Validation = () => {
  bindExhibitorStep1Validation()
  restoreSignupPasswordFields()
}

export { initExhibitorStep1Validation, hideStep1RequiredAlert, restoreSignupPasswordFields }
